import { createSlice } from '@reduxjs/toolkit';

export type CardMedia = {
  type: 'none' | 'image' | 'video';
  url: string;
  thumbnail?: string;
  alt?: string;
};

export type CardSection = {
  id: string;
  type: string;
  [key: string]: any;
};

export type CardGalleryItem = {
  id: string;
  url: string;
  alt?: string;
};

export type CardContent = {
  media: CardMedia;
  title: string;
  isTitle: boolean;
  description: string;
  isDescription: boolean;
  sections: CardSection[];
  buttons: any[];
  /* The lines and pictures only some templates print. They belong to the content payload
     like everything else — a template that is not the active one must still find its text
     where it left it. */
  badge: string;
  eyebrow: string;
  meta: string;
  price: string;
  note: string;
  noteLinkText: string;
  noteLinkUrl: string;
  gallery: CardGalleryItem[];
};

export const CARD_EMPTY_CONTENT: CardContent = {
  media: { type: 'none', url: '' },
  title: '',
  isTitle: true,
  description: '',
  isDescription: true,
  sections: [],
  buttons: [],
  badge: '',
  eyebrow: '',
  meta: '',
  price: '',
  note: '',
  noteLinkText: '',
  noteLinkUrl: '',
  gallery: [],
};

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  blockContent: {
    // Everything the user composes lives under `content` — the same nesting the API uses,
    // so the generic save path posts it back verbatim without a card-specific branch.
    content: CARD_EMPTY_CONTENT,
    heading: {} as Record<string, any>,
  },
  blockDesign: {
    view: 'card',
    layout: '1',
  },
  itemDesign: {} as Record<string, any>,
  containerDesign: {} as Record<string, any>,
};

export const cardSlice = createSlice({
  name: 'card',
  initialState,
  reducers: {
    cardStore: (state, action) => {
      const { id, isActive, sid, uid, data } = action.payload;

      const block = data ?? {};
      const { blockContent, blockDesign } = block;
      const { itemDesign, containerDesign } = blockDesign ?? {};
      const content = blockContent?.content ?? {};

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.itemDesign = itemDesign ?? {};
      state.containerDesign = containerDesign ?? {};

      /* Merge over the defaults rather than picking known keys out of the payload.
         `content` is a free-form JSON blob that grows with every template, and this
         reducer runs on every entry into the block — a whitelist here silently drops
         whatever it has not heard of, and the next content save writes that loss back
         into CardBlock.Data. */
      const merged = { ...CARD_EMPTY_CONTENT, ...content };

      state.blockContent = {
        content: {
          ...merged,
          // The blob is user data out of a JSON column, so a null where a list is
          // expected is possible; the editors map over these directly.
          media: merged.media ?? CARD_EMPTY_CONTENT.media,
          sections: Array.isArray(merged.sections) ? merged.sections : [],
          buttons: Array.isArray(merged.buttons) ? merged.buttons : [],
          gallery: Array.isArray(merged.gallery) ? merged.gallery : [],
        },
        heading: blockContent?.heading ?? {},
      };

      state.blockDesign = {
        view: blockDesign?.view ?? 'card',
        layout: blockDesign?.layout ?? '1',
      };
    },
    cardClear: () => initialState,

    cardUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    cardUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    cardUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },
    cardUpdateBlockDesign: (state, action) => {
      state.blockDesign = action.payload;
    },
  },
});

export const { cardStore, cardClear, cardUpdateIsLoading, cardUpdateIsActive, cardUpdateBlockContent, cardUpdateBlockDesign } = cardSlice.actions;

export const selectCardIsLoading = (state: any) => state.card.isLoading;

export const selectCardId = (state: any) => state.card.id;
export const selectCardUID = (state: any) => state.card.uid;
export const selectCardSID = (state: any) => state.card.sid;

export const selectCardBlockContent = (state: any) => state.card.blockContent;
export const selectCardBlockDesign = (state: any) => state.card.blockDesign;
export const selectCardContent = (state: any): CardContent => state.card.blockContent.content;
export const selectCardItemDesign = (state: any) => state.card.itemDesign;
export const selectCardContainerDesign = (state: any) => state.card.containerDesign;

export default cardSlice.reducer;
